import { Actions, Events } from "@events";
import { SignalingMessageTypes } from "./signaling";
import { sleep } from "@/utils";
import i18n from "@/i18n";

class RoomActionController {
  /**
   * @type {import("./webrtcRoom.js").default} room
   */
  room = null;
  constructor(room) {
    this.room = room;

    let localStreamPromiseResolve = null;
    this.localStreamPromise = new Promise((resolve) => {
      localStreamPromiseResolve = resolve;
    });

    let remoteStreamPromiseResolve = null;
    this.remoteStreamPromise = new Promise((resolve) => {
      remoteStreamPromiseResolve = resolve;
    });

    this.room.callEventEmitter.on(Events.OnLocalStream, (stream) => {
      this.localStream = stream;
      console.log("WebRTCRecorder: local stream ready");
      localStreamPromiseResolve(stream);
    });

    this.room.callEventEmitter.on(Events.OnRemoteStream, (stream) => {
      this.remoteStream = stream;
      console.log("WebRTCRecorder: remote stream ready");
      remoteStreamPromiseResolve(stream);
    });

    this.room.localActionEmitter.on(Actions.MuteAudio, async () => {
      await this.localStreamPromise;
      this.muteGeneric("Audio", true, false);
    });

    this.room.localActionEmitter.on(Actions.UnmuteAudio, async () => {
      await this.localStreamPromise;
      this.muteGeneric("Audio", false, false);
    });

    this.room.localActionEmitter.on(Actions.MuteVideo, async () => {
      await this.localStreamPromise;
      this.muteGeneric("Video", true, false);
    });

    this.room.localActionEmitter.on(Actions.UnmuteVideo, async () => {
      await this.localStreamPromise;
      this.muteGeneric("Video", false, false);
    });

    this.room.localActionEmitter.on(Actions.HoldCall, async () => {
      await this.localStreamPromise;
      this.holdCall(true);
    });

    this.room.localActionEmitter.on(Actions.ResumeCall, async () => {
      await this.localStreamPromise;
      this.resumeCall(true);
    });

    this.room.localActionEmitter.on(Actions.MuteRemoteVideo, async () => {
      await this.remoteStreamPromise;
      this.sendRemoteAction({
        isMute: true,
        type: "Video",
        sendTextChannelMessage: true,
      });
    });

    this.room.localActionEmitter.on(Actions.UnmuteRemoteVideo, async () => {
      await this.remoteStreamPromise;
      this.sendRemoteAction({
        isMute: false,
        type: "Video",
        sendTextChannelMessage: true,
      });
    });

    this.room.localActionEmitter.on(Actions.MuteRemoteAudio, async () => {
      await this.remoteStreamPromise;
      this.sendRemoteAction({
        isMute: true,
        type: "Audio",
        sendTextChannelMessage: true,
      });
    });

    this.room.localActionEmitter.on(Actions.UnmuteRemoteAudio, async () => {
      await this.remoteStreamPromise;
      this.sendRemoteAction({
        isMute: false,
        type: "Audio",
        sendTextChannelMessage: true,
      });
    });

    this.room.localActionEmitter.on(Actions.HangUp, this.hangUp.bind(this));
    this.room.callEventEmitter.on(Events.TextChannelMessageReceived, async (data) => {
      const { data: messageData } = data;
      const [command] = messageData.split("|"); // Split the message by pipe character and get the first element. The first element is the command name and the second element is the rtcId
      const COMMAND_LIST = {
        HOLD: "HOLD",
        PICKUP: "PICKUP",
        VIDEO_MUTE: "VIDEO_MUTE",
        VIDEO_UNMUTE: "VIDEO_UNMUTE",
        AUDIO_MUTE: "AUDIO_MUTE",
        AUDIO_UNMUTE: "AUDIO_UNMUTE",
        STATUS: "STATUS",
      };
      switch (command) {
        case COMMAND_LIST.HOLD:
          this.holdCall(false);
          break;
        case COMMAND_LIST.PICKUP:
          this.resumeCall(false);
          break;
        case COMMAND_LIST.VIDEO_MUTE:
          await this.localStreamPromise;
          this.muteGeneric("Video", true, true);
          break;
        case COMMAND_LIST.VIDEO_UNMUTE:
          await this.localStreamPromise;
          this.muteGeneric("Video", false, true);
          break;
        case COMMAND_LIST.AUDIO_MUTE:
          await this.localStreamPromise;
          this.muteGeneric("Audio", true, true);
          break;
        case COMMAND_LIST.AUDIO_UNMUTE:
          await this.localStreamPromise;
          this.muteGeneric("Audio", false, true);
          break;
        case COMMAND_LIST.STATUS:
          await this.remoteStreamPromise;
          const [_, type, status, rtcId] = messageData.split("|");
          if (rtcId !== this.room.state.rtcId) {
            return;
          }

          var setStatus = status === "MUTED" ? true : false;

          if (type === "VIDEO") {
            this.room.state.setRemoteVideoMuted(setStatus);

            // find remote video track and set enabled false/true
            var remotePeer = this.room.state.getPeerWithType("remote");
            if (!remotePeer) {
              console.error("No remote peer found");
              return;
            }

            const remoteStream = remotePeer.srcObject;
            remoteStream.getTracks().forEach((track) => {
              if (track.kind === "video") {
                track.enabled = !setStatus;
              }
            });
          } else if (type === "AUDIO") {
            this.room.state.setRemoteAudioMuted(setStatus);
            // find remote audio track and set enabled false/true
            var remotePeer = this.room.state.getPeerWithType("remote");
            if (!remotePeer) {
              console.error("No remote peer found");
              return;
            }

            const remoteStream = remotePeer.srcObject;
            remoteStream.getTracks().forEach((track) => {
              if (track.kind === "audio") {
                track.enabled = !setStatus;
              }
            });
          }

          break;
        default:
          break;
      }
    });
  }

  async muteGeneric(type, state, remote) {
    const op = state ? "Mute" : "Unmute";
    console.log(`${op} ${type} requested fromRemote ${remote}`);
    this.room.localStream.getTracks().forEach((track) => {
      if (track.kind === type.toLowerCase()) {
        // action = mute
        // source = remote
        // track is already muted, will not mute again
        if (remote && state && !track.enabled) {
          console.log("Track is already muted, will not mute again");
          this.sendStatusMessage(type);
          return;
        }

        // action = unmute
        // source = remote
        // track is not muted by remote, will not unmute
        if (remote && !state && !track.isMutedByRemote) {
          console.log("Track is not muted by remote, will not unmute");
          track.isMutedByRemote = false;
          this.sendStatusMessage(type);
          return;
        }

        delete track.isMutedByRemote;

        track.enabled = !state;
        if (remote && state) {
          track.isMutedByRemote = true;
        }

        this.room.state[`set${type}Muted`](state);

        this.sendStatusMessage(type);
      }
    });
  }

  async sendStatusMessage(type) {
    const status = this.room.state[`${type.toLowerCase()}Muted`];

    const message = `STATUS|${type.toUpperCase()}|${status ? "MUTED" : "UNMUTED"}|${this.room.state.rtcId}`;
    this.room.textChannel.send(message);
  }

  /**
   * The `sendRemoteAction` function is an asynchronous function that allows for muting or unmuting a
   * remote video or audio stream, and optionally sending a text channel message to the remote user.
   * @param {Object} options - An object containing the parameters `isMute`, `type`, and `sendTextChannelMessage`.
   * @param {boolean} options.isMute: A boolean indicating whether the remote action should mute or unmute the
   * stream. Default value is `true`.
   * @param {string} options.type: Stream type. Default value is `Video`. Valid values are `Video` and `Audio`.
   * @param {boolean} options.sendTextChannelMessage: A boolean indicating whether to send a text channel message
   * @returns The function does not explicitly return a value.
   */
  async sendRemoteAction({ isMute = true, type = "Video" } = {}) {
    if (type !== "Video" && type !== "Audio") {
      console.error("Invalid type ", type);
      return;
    }
    console.log(`Sending Remote Action : ${type}, isMute `, isMute);
    this.room.state[`setRemote${type}Muted`](isMute);

    var remotePeer = this.room.state.getPeerWithType("remote");

    const remoteStream = remotePeer.srcObject;
    remoteStream.getTracks().forEach((track) => {
      if (track.kind === type.toLowerCase()) {
        track.enabled = !isMute;
      }
    });

    const actionType = isMute ? `MUTE` : "UNMUTE";
    const message = `${type.toUpperCase()}_${actionType}|${this.room.state.rtcId}`;
    this.room.textChannel.send(message);
  }

  async holdCall(sendTextChannelMessage = true) {
    console.log("Hold call requested current hold state = " + this.room.state.isHeld);
    this.room.state.setIsHeld(true);

    // if sendTextChannelMessage is true the request is from local user
    this.muteGeneric("Audio", true, !sendTextChannelMessage);
    this.muteGeneric("Video", true, !sendTextChannelMessage);

    if (sendTextChannelMessage) {
      this.room.textChannel.send("HOLD|" + this.room.state.rtcId);
    }
  }

  async resumeCall(sendTextChannelMessage = true) {
    console.log("Resume call requested current hold state = " + this.room.state.isHeld);
    this.room.state.setIsHeld(false);

    // if sendTextChannelMessage is true the request is from local user
    this.muteGeneric("Audio", false, !sendTextChannelMessage);
    this.muteGeneric("Video", false, !sendTextChannelMessage);

    if (sendTextChannelMessage) {
      this.room.textChannel.send("PICKUP|" + this.room.state.rtcId);
    }
  }

  async hangUp(hangupData) {
    const { hangupBy } = hangupData;
    console.log("hangupBy:", hangupBy);

    if (hangupBy == "local") {
      await this.room.signaling.send({
        cmd: "send",
        msg: JSON.stringify({ type: SignalingMessageTypes.Bye }),
      });
    }

    let callEventPromise = Promise.resolve();

    try {
      // log room and room objects state (is null or not) before closing
      console.log("Hanging up the call, closing peer connection and signaling");
      console.log("Room state before hangup:", this.room || "Room is null");
      console.log("Room state object before hangup:", this.room?.state || "Room state is null");
      console.log("PeerConnection before hangup:", this.room?.peerConnection || "PeerConnection is null");
      console.log("Signaling before hangup:", this.room?.signaling || "Signaling is null");

      callEventPromise = this.room?.callEventEmitter?.emitAsync(Events.CallEvent, {
        name: "AgentVideoCallEnded",
        data: !!this.room?.state?.preventPageClose ? "PreventPageClose" : "PageClose",
      }) || Promise.resolve();

      this.room?.callEventEmitter?.emit(Events.VideoCallEnded, { hangupBy, preventClose: !!this.room?.state?.preventPageClose });


      this.room?.state?.addConnectionLog(hangupBy.toUpperCase() + "-HANGUP");

      this.room?.state?.setIsDisconnected(true);

      if (this.room?.state?.preventPageClose) {
        console.log("Prevent page close is enabled, will not redirect");
        return;
      }
    } catch (error) {
      console.error("Error during hangup process:", error);
    }

    if (this.room?.state?.role.toUpperCase() == "SUPERVISOR") {
      this.room?.peerConnection?.close();
      this.room?.signaling?.disconnect();
      if (hangupBy == "remote") {
        this.room.state.snackbar.msg = i18n.global.t("TheCallHasEnded");
        this.room.state.snackbar.show = true;
        await sleep(5000);
      }
      await callEventPromise;
      window.close();
    }

    if (window.CONFIG.CLOSING_URL[this.room?.state?.role.toUpperCase() || "USER"] == undefined) {
      this.room?.peerConnection?.close();
      this.room?.signaling?.disconnect();
      console.error("No closing URL defined for role: " + this.room?.state?.role.toUpperCase());
      return;
    }

    await callEventPromise;
    window.location = window.CONFIG.CLOSING_URL[this.room?.state?.role.toUpperCase() || "USER"];
  }
}

export { RoomActionController };
